import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserDetailsObject } from "../models/interfaces";
import { RootState } from "../utils/store";

export interface GeneralState {
  edit: boolean;
  userName: string;
  contactNumber: string;
  payNowEnabled: boolean;
  splitPayEnabled: boolean;
  orderingEnabled: boolean;
  groupByTicket: boolean;
  isSocketConnection: boolean | null;
  hardReloadFlag: string;
  isLocationOnline: boolean;
  isUTTLocked: boolean;
  checkoutState: {
    buttonText: string;
    retryMode: number;
    errorIn: string;
  };
  otpRequired: boolean;
}

const initialState: GeneralState = {
  edit: false,
  userName: "",
  contactNumber: "",
  payNowEnabled: false,
  splitPayEnabled: false,
  orderingEnabled: false,
  groupByTicket: false,
  isSocketConnection: null,
  hardReloadFlag: "idemp-release",
  isLocationOnline: true,
  isUTTLocked: false,
  checkoutState: {
    buttonText: "Checkout",
    retryMode: 0,
    errorIn: "",
  },
  otpRequired: false,
};
export const generalSlice = createSlice({
  name: "general",
  initialState,
  reducers: {
    isFieldEdited: (state, action: PayloadAction<boolean>) => {
      state.edit = action.payload;
    },
    addUserDetails: (state, action: PayloadAction<UserDetailsObject>) => {
      state.userName = action.payload.name;
      state.contactNumber = action.payload.mobile;
    },
    updatePayNowEnabledStatus: (state, action: PayloadAction<boolean>) => {
      state.payNowEnabled = action.payload;
    },
    updateSplitPayEnabledStatus: (state, action: PayloadAction<boolean>) => {
      state.splitPayEnabled = action.payload;
    },
    updateOrderingEnabledStatus: (state, action: PayloadAction<boolean>) => {
      state.orderingEnabled = action.payload;
    },
    clearUserDetails: (state) => {
      state.edit = false;
      state.userName = "";
      state.contactNumber = "";
    },
    addFlagforSocket: (state, action: PayloadAction<boolean>) => {
      state.isSocketConnection = action.payload;
    },
    addHardReloadFlag: (state, action: PayloadAction<string>) => {
      state.hardReloadFlag = action.payload;
    },
    updateIsLocationOnline: (state, action: PayloadAction<boolean>) => {
      state.isLocationOnline = action.payload;
    },
    updateIsUTTLocked: (state, action: PayloadAction<boolean>) => {
      state.isUTTLocked = action.payload;
    },
    updateCheckoutState: (
      state,
      action: PayloadAction<{
        buttonText: string;
        retryMode: number;
        errorIn: string;
      }>
    ) => {
      state.checkoutState = action.payload;
    },
    updateOTPRequired: (state, action: PayloadAction<boolean>) => {
      state.otpRequired = action.payload;
    },
    updateGroupByTicket: (state, action: PayloadAction<boolean>) => {
      state.groupByTicket = action.payload;
    },
  },
});

export const {
  updateSplitPayEnabledStatus,
  updatePayNowEnabledStatus,
  updateOrderingEnabledStatus,
  isFieldEdited,
  addUserDetails,
  clearUserDetails,
  addFlagforSocket,
  addHardReloadFlag,
  updateIsLocationOnline,
  updateIsUTTLocked,
  updateCheckoutState,
  updateOTPRequired,
  updateGroupByTicket,
} = generalSlice.actions;

export const selectFieldEdit = (state: RootState) => state.general.edit;
export const selectUserName = (state: RootState) => state.general.userName;
export const selectContactNumber = (state: RootState) =>
  state.general.contactNumber;
export const selectPayNowEnabledStatus = (state: RootState) =>
  state.general.payNowEnabled;
export const selectSplitPayEnabledStatus = (state: RootState) =>
  state.general.splitPayEnabled;
export const selectOrderingEnabledStatus = (state: RootState) =>
  state.general.orderingEnabled;
export const selectHardReloadFlag = (state: RootState) =>
  state.general.hardReloadFlag;
export const selectIsLocationOnline = (state: RootState) =>
  state.general.isLocationOnline;
export const selectIsUTTLocked = (state: RootState) =>
  state.general.isUTTLocked;
export const selectCheckoutState = (state: RootState) =>
  state.general.checkoutState;
export const selectOTPRequired = (state: RootState) =>
  state.general.otpRequired;
export const selectTicketByGroup = (state: RootState) =>
  state.general.groupByTicket;

export default generalSlice.reducer;
